/**
 * Member-status for a Drop's onboarding door, same contract as the challenge
 * hook. Fires with a partner handshake or a signed-in session.
 */
import {useQuery} from "@tanstack/react-query";
import {useSelector} from "react-redux";
import {DropMemberStatusResponse, DropsApi} from "@devour/client";
import getConfig from "@/utils/getConfig";
import {IStore} from "@/redux/defaultStore";

export function useGetDropMemberStatus(slug: string | undefined, enabled: boolean) {
    const realmUuid = useSelector((store: IStore) => store.rttrStore?.realmUuid);
    const realmTs = useSelector((store: IStore) => store.rttrStore?.realmTs);
    const realmSig = useSelector((store: IStore) => store.rttrStore?.realmSig);

    const hasHandoff = Boolean(realmUuid && realmTs && realmSig);
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    return useQuery({
        /* realmTs in the key: a re-embed with a fresh timestamp must refetch. */
        queryKey: ["drop-member-status", slug, realmUuid ?? "no-handoff", realmTs ?? "no-ts"],
        queryFn: async (): Promise<DropMemberStatusResponse> => {
            const api = new DropsApi(getConfig(fullToken));
            return api.getDropsSlugMemberStatus({
                slug: slug!,
                xRealmUUID: realmUuid!,
                xRealmTS: realmTs!,
                xRealmSig: realmSig!,
            });
        },
        /*
         * A handshake OR a session — gating on the handshake alone meant an
         * EMAIL_CODE door could never read its own member status, so its
         * surface never learned which terms to collect.
         */
        enabled: Boolean(slug) && (hasHandoff || Boolean(fullToken)) && enabled,
        staleTime: 60_000,
    });
}
